import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AttributesValueType, CompanyAttributeDTO } from '@iota/iota-management-portal-v4-client';
import moment from 'moment';
import { AttributeValueTypeDescriptions, AttributeValueTypes } from '../../constants-and-enums/attribute-value-types';

const VALUE_SEPARATOR = '#';

enum NumberType {
    FLOAT,
    INTEGER
}

@Component({
    standalone: false,
    selector: 'app-attribute',
    templateUrl: './attribute.component.html',
    styleUrls: ['./attribute.component.scss']
})
export class AttributeComponent implements OnInit, OnChanges {
    @Input() attribute: CompanyAttributeDTO;
    @Input() set disabled(disabledValue: boolean) {
        if (this.attributeForm) {
            if (disabledValue) {
                this.attributeForm.disable();
            } else {
                this.attributeForm.enable();
            }

            if (this.attribute.attributeId) {
                this.attributeForm.controls.attributeValueType.disable();
            }
        }
        this.disabledValue = disabledValue;
    }

    get disabled(): boolean {
        return this.disabledValue;
    }

    public get invalid(): boolean {
        return (
            this.attributeForm.invalid ||
            this.attributeForm.controls.attributeValue.value.trim() === '' ||
            this.attributeForm.controls.attributeName.value.trim() === ''
        );
    }

    @Input() deletable = true;

    @Output() delete = new EventEmitter<void>();

    attributeForm: UntypedFormGroup;
    private disabledValue: boolean;

    attributeValueTypes = AttributeValueTypes;
    error: string;

    updatedValueType: AttributesValueType;
    updatedValue = '';
    updatedName = '';
    updatedLocale = '';

    constructor(private datePipe: DatePipe) {}

    resetAttribute() {
        this.updatedValueType = this.attribute.valueType;
        this.updatedName = this.attribute.attributeName || '';
        this.updatedLocale = this.attribute.locale || '';

        this.attributeForm = new UntypedFormGroup({
            attributeName: new UntypedFormControl(this.updatedName, Validators.required),
            attributeValueType: new UntypedFormControl(this.attribute.valueType, Validators.required),
            attributeValue: new UntypedFormControl(this.parseAttributeValue(), Validators.required),
            attributeLocale: new UntypedFormControl(this.updatedLocale)
        });

        if (this.disabledValue) {
            this.attributeForm.disable();
        } else {
            this.attributeForm.enable();
        }

        if (this.attribute.attributeId) {
            this.attributeForm.controls.attributeValueType.disable();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const newAttribute = changes?.['attribute']?.currentValue;

        if (newAttribute) {
            this.resetAttribute();
        }
    }

    ngOnInit(): void {
        this.resetAttribute();

        this.attributeForm.valueChanges.subscribe((value) => {
            this.attributeForm.controls.attributeValue.setErrors(null);

            // We need to extract the value type this way in case the input is disabled,
            // because then value.attributeValueType is null
            const valueType = this.attributeForm.getRawValue()?.attributeValueType;

            this.updatedValueType = valueType;
            this.updatedValue = value.attributeValue;
            this.updatedName = value.attributeName.trim();

            if (value.attributeLocale && valueType === AttributesValueType.STRING && value.attributeLocale.trim() !== '') {
                this.updatedLocale = value.attributeLocale;
            }
        });
    }

    getUpdatedAttribute(): CompanyAttributeDTO {
        if (this.attributeForm.dirty || !this.attribute.attributeId)
            return {
                ...this.attribute,
                attributeName: this.updatedName,
                valueType: this.updatedValueType,
                locale: this.updatedLocale,
                ...this.getAttributeValues(this.updatedValue)
            };

        return null;
    }

    getValueTypeDescription(type: string): string {
        return AttributeValueTypeDescriptions.get(type);
    }

    private parseAttributeValue(): string {
        switch (this.updatedValueType) {
            case AttributesValueType.BOOL:
                return this.joinValues(this.attribute.booleanValue);
            case AttributesValueType.DATE:
                return this.joinDateValues(this.attribute.dateValue);
            case AttributesValueType.DATETIME:
                return this.joinDateTimeValues(this.attribute.dateTimeValue);
            case AttributesValueType.NUMBER:
                return this.joinValues(this.attribute.numValue);
            case AttributesValueType.INTEGER:
                return this.joinValues(this.attribute.intValue);
            case AttributesValueType.STRING:
                return this.joinValues(this.attribute.strValue);
            case AttributesValueType.UUID:
                return this.joinValues(this.attribute.uuidValue);
            default:
                return '';
        }
    }

    private joinValues(values: any[]): string {
        return Array.isArray(values) ? values.join(VALUE_SEPARATOR) : '';
    }

    private joinDateValues(values: Date[]): string {
        return Array.isArray(values) ? values.map((v) => this.datePipe.transform(v, 'dd.MM.yyyy')).join(VALUE_SEPARATOR) : '';
    }

    private joinDateTimeValues(values: Date[]): string {
        return Array.isArray(values) ? values.map((v) => this.datePipe.transform(v, 'dd.MM.yyyy HH:mm:ss')).join(VALUE_SEPARATOR) : '';
    }

    private getAttributeValues(internalValue: string): CompanyAttributeDTO {
        const attributeValues: CompanyAttributeDTO = {};

        switch (this.attributeForm.controls.attributeValueType.value as AttributesValueType) {
            case AttributesValueType.BOOL:
                attributeValues.booleanValue = this.parseToBooleanArray(internalValue);
                break;
            case AttributesValueType.DATE:
                attributeValues.dateValue = this.parseToDateArray(internalValue, 'DD.MM.YYYY');
                break;
            case AttributesValueType.DATETIME:
                attributeValues.dateTimeValue = this.parseToDateArray(internalValue, 'DD.MM.YYYY hh:mm:ss');
                break;
            case AttributesValueType.NUMBER:
                attributeValues.numValue = this.parseToNumberArray(internalValue, NumberType.FLOAT);
                break;
            case AttributesValueType.INTEGER:
                attributeValues.intValue = this.parseToNumberArray(internalValue, NumberType.INTEGER);
                break;
            case AttributesValueType.STRING:
                attributeValues.strValue = this.parseToStringArray(internalValue);
                break;
            case AttributesValueType.UUID:
                attributeValues.uuidValue = this.parseToStringArray(internalValue);
                break;
        }

        return attributeValues;
    }

    private parseToStringArray(stringValue: string): Array<string> {
        return stringValue.split(VALUE_SEPARATOR).filter((value) => value.trim() !== '');
    }

    private parseToDateArray(stringValue: string, format: string): Array<Date> {
        return stringValue
            .split(VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((dateString) => {
                if (moment(dateString, format).isValid()) {
                    return moment.utc(dateString, format) as any;
                }

                this.setParseError();
                return undefined;
            });
    }

    private parseToBooleanArray(stringValue: string): Array<boolean> {
        return stringValue
            .split(VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((value) => {
                if (value.trim() === 'true') {
                    return true;
                }

                if (value.trim() === 'false') {
                    return false;
                }

                this.setParseError();
                return undefined;
            });
    }

    private parseToNumberArray(stringValue: string, type: NumberType): Array<number> {
        return stringValue
            .split(VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((value) => (type === NumberType.INTEGER ? this.parseToInt(value) : this.parseToFloat(value)));
    }

    private parseToInt(stringValue: string): number {
        if (!isNaN(parseInt(stringValue))) {
            return parseInt(stringValue);
        }
        this.setParseError();
        return undefined;
    }

    private parseToFloat(stringValue: string): number {
        if (!isNaN(parseFloat(stringValue))) {
            return parseFloat(stringValue);
        }
        this.setParseError();
        return undefined;
    }

    private setParseError() {
        this.attributeForm.controls.attributeValue.setErrors({
            parsingFailed: true
        });
    }
}
